import { LockKeyhole } from 'lucide-react';
import { useState } from 'react';

import { messageLisible } from '@/shared/api/erreurs';
import { useSession } from '@/shared/auth/sessionStore';
import { Logo } from '@/shared/ui/Logo';
import { Bouton } from '@/shared/ui/Bouton';
import { Aide, Etiquette, ErreurChamp, Saisie } from '@/shared/ui/Champ';
import { Carte } from '@/shared/ui/Table';

/**
 * Connexion a l'administration de plateforme.
 *
 * SEULE PAGE ATTEIGNABLE SANS SESSION, et elle n'est pas « publique » pour autant : elle
 * ne fait que soumettre des identifiants. Aucune route de cette application n'expose de
 * donnee sans authentification (§1).
 *
 * <para>
 * Le message d'echec ne distingue jamais « compte inconnu » de « mot de passe faux » ni de
 * « compte non administrateur » — cette derniere distinction serait la plus couteuse :
 * elle permettrait d'enumerer les comptes d'exploitation depuis l'exterieur.
 * </para>
 */
export function ConnexionPage() {
  const connecter = useSession((etat) => etat.connecter);
  const connexionEnCours = useSession((etat) => etat.connexionEnCours);

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-fond px-4">
      <div className="w-full max-w-sm space-y-5">
        <div className="flex flex-col items-center gap-2">
          <Logo taille="md" />
          <span className="rounded-full border border-bordure bg-surface-haute px-2 py-px text-[0.6875rem] text-texte-doux">
            Administration
          </span>
        </div>

        <Carte className="p-5">
          <form
            className="space-y-4"
            noValidate
            onSubmit={(evenement) => {
              evenement.preventDefault();
              setErreur(null);

              void connecter(email.trim(), motDePasse).catch((cause: unknown) =>
                setErreur(messageLisible(cause)),
              );
            }}
          >
            <div className="space-y-1.5">
              <Etiquette htmlFor="email">Adresse e-mail</Etiquette>
              <Saisie
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(evenement) => setEmail(evenement.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Etiquette htmlFor="motDePasse">Mot de passe</Etiquette>
              <Saisie
                id="motDePasse"
                type="password"
                autoComplete="current-password"
                required
                value={motDePasse}
                onChange={(evenement) => setMotDePasse(evenement.target.value)}
              />
            </div>

            <ErreurChamp>{erreur}</ErreurChamp>

            <Bouton type="submit" className="w-full" disabled={connexionEnCours}>
              {connexionEnCours ? 'Connexion…' : 'Se connecter'}
            </Bouton>
          </form>
        </Carte>

        {/*
          Le §3 recommande une authentification renforcee pour cet espace et demande de la
          prevoir dans le flux, meme si l'implementation vient plus tard. Elle est annoncee
          ici plutot que passee sous silence : l'exploitant doit savoir que sa session n'est
          aujourd'hui protegee que par un mot de passe.
        */}
        <p className="flex items-start gap-2 rounded-[--radius-astro] border border-alerte/30 bg-alerte-voile px-3 py-2">
          <LockKeyhole className="mt-0.5 size-3.5 shrink-0 text-alerte" aria-hidden />
          <span className="text-texte-doux">
            <strong className="text-texte">Second facteur non encore actif.</strong> Cet espace
            donne acces a toutes les entreprises ; il est prevu de le proteger par un second
            facteur, non implemente a ce jour.
          </span>
        </p>

        <Aide className="text-center">
          Les sessions ne survivent pas a la fermeture de l’onglet.
        </Aide>
      </div>
    </div>
  );
}
